import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Star,
    Calendar,
    Briefcase,
    User,
    ChevronLeft,
    ChevronRight,
    Loader2,
    AlertCircle,
    MessageSquare,
    Send
} from "lucide-react";
import { Card } from "./card";
import { useAppDispatch, useAppSelector } from "../../redux";
import { fetchReviewsByAuthor, updateReview } from "../../redux/Slices/reviewSlice";
import { calculateUserAverageRating } from "../../utils/ratingCalculationUtils";
import { RoleEnum } from "../../modules";
import VoteDisplayButton from "./VoteDisplayButton";
import ReplyVoteButton from "./ReplyVoteButton";
import { useTranslation } from "react-i18next";

interface ClientReviewsTabProps {
    userId: string;
    isAdminEdit?: boolean;
}

interface ReviewDisplay {
    id: string;
    authorName: string;
    authorPhoto?: string;
    rating: number;
    text: string;
    reply?: string;
    canReply?: boolean;
    createdAt: string;
    milestone?: {
        id: string;
        description?: string;
    };
    service?: string;
    status?: string;
    review?: any[] | null;
}

const ClientReviewsTab: React.FC<ClientReviewsTabProps> = ({ userId, isAdminEdit = false }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { userReviews, userReviewsLoading, userReviewsError, userReviewsTotalCount, userReviewsHasNextPage } = useAppSelector((state: any) => state.reviews);
    const { user } = useAppSelector((state: any) => state.auth);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [reviewsPerPage] = useState(10);
    const [expandedReviews, setExpandedReviews] = useState<Set<string>>(new Set());
    
    // Reply state
    const [replyingToReview, setReplyingToReview] = useState<string | null>(null);
    const [replyText, setReplyText] = useState("");
    const [isSubmittingReply, setIsSubmittingReply] = useState(false);
    const [expandedNestedReviews, setExpandedNestedReviews] = useState<Set<string>>(new Set());

    // Star rating display component
    const StarRatingDisplay: React.FC<{ rating: number; size?: "sm" | "md" | "lg" }> = ({ rating, size = "sm" }) => {
        const sizeClasses = {
            sm: "h-4 w-4",
            md: "h-5 w-5",
            lg: "h-6 w-6"
        };

        return (
            <div className="flex items-center">
                {[1, 2, 3, 4, 5].map((star) => {
                    const isHalfFilled = rating >= star - 0.5 && rating < star;
                    const isFilled = rating >= star;

                    return (
                        <div key={star} className="relative">
                            <Star
                                className={`${sizeClasses[size]} ${isFilled
                                    ? "fill-yellow-400 text-yellow-400"
                                    : "text-gray-300"
                                    }`}
                            />
                            {isHalfFilled && (
                                <div className="absolute left-0 top-0 w-1/2 h-full overflow-hidden">
                                    <Star
                                        className={`${sizeClasses[size]} fill-yellow-400 text-yellow-400`}
                                    />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    // Transform review data for display
    const transformReviewForDisplay = (review: any): ReviewDisplay => {
        const authorName = review.author?.firstName && review.author?.lastName
            ? `${review.author.firstName} ${review.author.lastName}`
            : review.author?.id || t("clientReviewsTab.anonymous");

        return {
            id: review.id,
            authorName,
            authorPhoto: review.author?.photo?.path || review.author?.photo?.url,
            rating: review.rating || 0,
            text: review.text || "",
            reply: review.reply || "",
            canReply: review.canReply || false,
            createdAt: review.createdAt,
            milestone: review.milestone ? {
                id: review.milestone.id,
                description: review.milestone.description
            } : undefined,
            service: review.author?.title || "Title",
            status: review.status?.name || "published",
            review: review.review || null
        };
    };

    // Load reviews on component mount and when userId changes
    useEffect(() => {
        if (userId) {
            dispatch(fetchReviewsByAuthor({
                authorId: userId,
                query: {
                    page: currentPage,
                    limit: reviewsPerPage,
                    sort: [{ orderBy: "createdAt", order: "DESC" }]
                }
            }));
        }
    }, [userId, currentPage, dispatch]);

    // Reset to page 1 when userId changes
    useEffect(() => {
        setCurrentPage(1);
    }, [userId]);

    // Calculate average rating
    const averageRating = userReviews?.length > 0
        ? userReviews.reduce((sum: number, review: any) => sum + (review.rating || 0), 0) / userReviews.length
        : 0;

    // Handle page change
    const handlePageChange = (page: number) => {
        if (page >= 1) {
            setCurrentPage(page);
        }
    };

    // Handle review text expansion
    const toggleReviewExpansion = (reviewId: string) => {
        setExpandedReviews(prev => {
            const newSet = new Set(prev);
            if (newSet.has(reviewId)) {
                newSet.delete(reviewId);
            } else {
                newSet.add(reviewId);
            }
            return newSet;
        });
    };

    // Toggle nested reviews expansion
    const toggleNestedReviews = (reviewId: string) => {
        setExpandedNestedReviews(prev => {
            const newSet = new Set(prev);
            if (newSet.has(reviewId)) {
                newSet.delete(reviewId);
            } else {
                newSet.add(reviewId);
            }
            return newSet;
        });
    };

    // Check if client can reply to a review
    const canClientReply = (displayReview: ReviewDisplay) => {
        if (!user || Number(user.role?.id) !== RoleEnum.client) return false;
        
        // Use the canReply field from the review object
        return displayReview.canReply === true;
    };

    // Handle reply submission
    const handleReplySubmit = async (reviewId: string) => {
        if (!replyText.trim() || isSubmittingReply) return;
        
        setIsSubmittingReply(true);
        try {
            await dispatch(updateReview({ id: reviewId, data: { reply: replyText.trim() } }));
            
            // Refresh reviews to get updated data
            dispatch(fetchReviewsByAuthor({ authorId: userId, query: { page: currentPage, limit: reviewsPerPage } }));
            
            setReplyingToReview(null);
            setReplyText("");
        } catch (error) {
            console.error("Failed to submit reply:", error);
        } finally {
            setIsSubmittingReply(false);
        }
    };

    // Handle reply cancel
    const handleReplyCancel = () => {
        setReplyingToReview(null);
        setReplyText("");
    };

    // Handle vote submission
    const handleVote = async (reviewId: string, voteType: "helpful" | "unhelpful") => {
        try {
            // You would typically call an API here to submit the vote
            console.log(`Voting ${voteType} for review ${reviewId}`);
            // Example: await dispatch(updateReviewVote({ reviewId, voteType }));
        } catch (error) {
            console.error("Error submitting vote:", error);
        }
    };


    // Calculate pagination with fallback for hasNextPage
    const totalPages = Math.ceil((userReviewsTotalCount || 0) / reviewsPerPage);
    const shouldShowPagination = userReviewsTotalCount > reviewsPerPage;
    const hasPreviousPage = currentPage > 1;
    const hasNextPage = userReviewsHasNextPage ?? (currentPage * reviewsPerPage < (userReviewsTotalCount || 0));

    // Debug logging
    console.log("ClientReviewsTab Debug:", {
        userReviewsTotalCount,
        userReviewsHasNextPage,
        hasNextPage,
        currentPage,
        reviewsPerPage,
        totalPages,
        shouldShowPagination
    });

    if (userReviewsLoading) {
        return (
            <Card className="p-6 w-full">
                <div className="flex items-center justify-center py-12">
                    <div className="flex flex-col items-center gap-4">
                        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                        <p className="text-gray-600">{t("clientReviewsTab.loadingReviews")}</p>
                    </div>
                </div>
            </Card>
        );
    }

    if (userReviewsError) {
        return (
            <Card className="p-6 w-full">
                <div className="flex items-center justify-center py-12">
                    <div className="flex flex-col items-center gap-4">
                        <AlertCircle className="h-8 w-8 text-red-500" />
                        <p className="text-red-600">{t("clientReviewsTab.failedToLoadReviews")}</p>
                        <p className="text-sm text-gray-500">{userReviewsError}</p>
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <Card className="p-6 w-full">
            <div className="mb-6">
                <h2 className="text-lg font-semibold mb-2">{t("clientReviewsTab.reviews")}</h2>

                {/* Average Rating Display */}
                {userReviews && userReviews.length > 0 && (
                    <div className="flex items-center gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                            <StarRatingDisplay rating={averageRating} size="md" />
                            <span className="text-2xl font-bold text-gray-900">
                                {averageRating.toFixed(1)}
                            </span>
                        </div>
                        <div className="text-sm text-gray-600">
                            {t("clientReviewsTab.basedOn")} {userReviews.length} {userReviews.length !== 1 ? t("clientReviewsTab.reviews_plural") : t("clientReviewsTab.review")}
                        </div>
                    </div>
                )}
            </div>

            {/* Reviews List */}
            <div className="space-y-4">
                {userReviews && userReviews.length > 0 ? (
                    userReviews.map((review: any) => {
                        const displayReview = transformReviewForDisplay(review);

                        return (
                            <motion.div
                                key={displayReview.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                            >
                                <div className="flex justify-between items-start gap-4 mb-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 flex-shrink-0 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                                            {displayReview.authorPhoto ? (
                                                <img
                                                    src={displayReview.authorPhoto}
                                                    alt={displayReview.authorName}
                                                    className="w-10 h-10 flex-shrink-0 rounded-full object-cover"
                                                />
                                            ) : (
                                                <User className="w-5 h-5 flex-shrink-0 text-gray-600" />
                                            )}
                                        </div>
                                        <div>
                                            <h3 className="font-medium text-gray-900 text-sm">
                                                {displayReview.authorName}
                                            </h3>
                                            <p className="text-xs text-gray-500">
                                                {displayReview.service}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <StarRatingDisplay rating={displayReview.rating} size="sm" />
                                        <span className="font-medium text-sm text-gray-900">
                                            {displayReview.rating.toFixed(1)}
                                        </span>
                                    </div>
                                </div>

                                <div className="mb-3 text-xs">
                                    <div className="items-center gap-4 text-gray-600">
                                        {displayReview.milestone?.description && (
                                            <div className="flex items-center gap-1 mb-2">
                                                <Briefcase className="w-4 h-4" />
                                                <span>{displayReview.milestone.description}</span>
                                            </div>
                                        )}
                                        <div className="flex items-center gap-1">
                                            <Calendar className="w-4 h-4" />
                                            <span>{new Date(displayReview.createdAt).toLocaleDateString()}</span>
                                        </div>

                                    </div>
                                </div>

                                {displayReview.text && (
                                    <p className="text-gray-600 text-sm leading-relaxed">
                                        {displayReview.text && displayReview.text.length > 200 ? (
                                            <span>
                                                {expandedReviews.has(displayReview.id) ? displayReview.text : `${displayReview.text.substring(0, 200)}...`}
                                                <button
                                                    onClick={() => toggleReviewExpansion(displayReview.id)}
                                                    className="ml-2 text-blue-600 hover:text-blue-800 font-medium"
                                                >
                                                    {expandedReviews.has(displayReview.id) ? t("clientReviewsTab.showLess") : t("clientReviewsTab.seeMore")}
                                                </button>
                                            </span>
                                        ) : (
                                            displayReview.text
                                        )}
                                    </p>
                                )}


                                {/* Vote Display Button */}
                                <div className="mt-3">
                                    <VoteDisplayButton
                                        reviewId={displayReview.id}
                                        votes={review.votes || []}
                                        currentUserId={user?.id}
                                        className=""
                                    />
                                </div>

                                {/* Reply Button and Form */}
                                <div className="mt-3 flex items-center gap-4">
                                    {/* See More Button - Show when there are nested reviews */}
                                    {(displayReview.review && Array.isArray(displayReview.review) && displayReview.review.length > 0 || displayReview.reply) && (
                                        <button
                                            onClick={() => toggleNestedReviews(displayReview.id)}
                                            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 hover:underline"
                                        >
                                            <MessageSquare className="h-4 w-4" />
                                            {expandedNestedReviews.has(displayReview.id) ? t("clientReviewsTab.showLessButton") : t("clientReviewsTab.seeMoreWithCount", { count: displayReview.review?.length || 0 })}
                                        </button>
                                    )}

                                    {/* Reply Button - Only show for clients who can reply */}
                                    {canClientReply(displayReview) && !displayReview.reply && (
                                        <button
                                            onClick={() => setReplyingToReview(displayReview.id)}
                                            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 hover:underline"
                                        >
                                            <MessageSquare className="h-4 w-4" />
                                            {t("clientReviewsTab.reply")}
                                        </button>
                                    )}
                                </div>

                                {/* Reply Form */}
                                {replyingToReview === displayReview.id && (
                                    <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                        <div className="mb-3">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {t("clientReviewsTab.yourReply")}
                                            </label>
                                            <textarea
                                                value={replyText}
                                                onChange={(e) => setReplyText(e.target.value)}
                                                placeholder={t("clientReviewsTab.replyPlaceholder")}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                                rows={3}
                                                maxLength={500}
                                            />
                                            <div className="text-xs text-gray-500 mt-1">
                                                {replyText.length}/500 {t("clientReviewsTab.characters")}
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleReplySubmit(displayReview.id)}
                                                disabled={!replyText.trim() || isSubmittingReply}
                                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                            >
                                                <Send className="h-4 w-4" />
                                                {isSubmittingReply ? t("clientReviewsTab.submitting") : t("clientReviewsTab.submitReply")}
                                            </button>
                                            <button
                                                onClick={handleReplyCancel}
                                                disabled={isSubmittingReply}
                                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                            >
                                                {t("clientReviewsTab.cancel")}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Nested Reviews Display */}
                                {expandedNestedReviews.has(displayReview.id) && displayReview.review && Array.isArray(displayReview.review) && displayReview.review.length > 0 && (
                                    <div className="mt-4 pl-6 border-l-4 border-gray-200">
                                        <div>
                                            <h4 className="text-sm font-medium text-gray-700 mb-3">{t("clientReviewsTab.relatedReviewsWithCount", { count: displayReview.review?.length || 0 })}</h4>
                                            <div className="space-y-3">
                                                {displayReview.review.map((nestedReview: any, index: number) => (
                                                    <div key={index} className="p-3 bg-gray-50 rounded-lg">
                                                        <div className="flex items-start gap-3">
                                                            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                                                                <User className="h-4 w-4 flex-shrink-0 text-gray-500" />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <h5 className="font-medium text-gray-900 text-sm">
                                                                        {nestedReview.author?.firstName && nestedReview.author?.lastName
                                                                            ? `${nestedReview.author.firstName} ${nestedReview.author.lastName}`.trim()
                                                                            : nestedReview.author?.id || t("clientReviewsTab.anonymous")}
                                                                    </h5>
                                                                    <div className="flex items-center gap-1">
                                                                        <StarRatingDisplay rating={nestedReview.rating || 0} size="sm" />
                                                                        <span className="text-xs text-gray-500">{nestedReview.rating || 0}/5</span>
                                                                    </div>
                                                                </div>
                                                                <p className="text-xs text-gray-500 mb-2">
                                                                    {new Date(nestedReview.createdAt || Date.now()).toLocaleDateString()}
                                                                </p>
                                                                {nestedReview.text && (
                                                                    <p className="text-gray-700 text-sm leading-relaxed">
                                                                        {nestedReview.text}
                                                                    </p>
                                                                )}
                                                                {nestedReview.reply && (
                                                                    <div className="mt-2 bg-blue-50 p-3 rounded-lg border border-blue-100">
                                                                        <p className="text-sm font-medium text-blue-900">
                                                                            {user && Number(user.role?.id) === RoleEnum.client ? t("clientReviewsTab.clientResponse") : t("clientReviewsTab.officialResponse")}
                                                                        </p>
                                                                        <p className="mt-1 text-sm text-blue-800">{nestedReview.reply}</p>
                                                                        <ReplyVoteButton
                                                                            reviewId={nestedReview.id}
                                                                            hasReply={!!nestedReview.reply}
                                                                            votes={nestedReview.votes || []}
                                                                            currentUserId={user?.id}
                                                                        />
                                                                    </div>
                                                                )}

                                                                {/* Reply button for nested reviews */}
                                                                {canClientReply(nestedReview) && !nestedReview.reply && (
                                                                    <div className="mt-3">
                                                                        <button
                                                                            onClick={() => setReplyingToReview(`${displayReview.id}-${index}`)}
                                                                            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 hover:underline"
                                                                        >
                                                                            <MessageSquare className="h-4 w-4" />
                                                                            {t("clientReviewsTab.reply")}
                                                                        </button>
                                                                    </div>
                                                                )}

                                                                {/* Reply form for nested reviews */}
                                                                {replyingToReview === `${displayReview.id}-${index}` && (
                                                                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                                                        <div className="mb-3">
                                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                                {t("clientReviewsTab.yourReply")}
                                                                            </label>
                                                                            <textarea
                                                                                value={replyText}
                                                                                onChange={(e) => setReplyText(e.target.value)}
                                                                                placeholder={t("clientReviewsTab.replyPlaceholder")}
                                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                                                                rows={3}
                                                                                maxLength={500}
                                                                            />
                                                                            <div className="text-xs text-gray-500 mt-1">
                                                                                {replyText.length}/500 {t("clientReviewsTab.characters")}
                                                                            </div>
                                                                        </div>
                                                                        <div className="flex gap-2">
                                                                            <button
                                                                                onClick={() => handleReplySubmit(nestedReview.id)}
                                                                                disabled={!replyText.trim() || isSubmittingReply}
                                                                                className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                                                            >
                                                                                <MessageSquare className="h-3 w-3" />
                                                                                {isSubmittingReply ? t("clientReviewsTab.submitting") : t("clientReviewsTab.submitReply")}
                                                                            </button>
                                                                            <button
                                                                                onClick={handleReplyCancel}
                                                                                disabled={isSubmittingReply}
                                                                                className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                                                            >
                                                                                {t("clientReviewsTab.cancel")}
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="pt-4">
                                            {/* Reply Section */}
                                            {displayReview.reply && (
                                                <div className="mt-4 bg-blue-50 p-4 rounded-lg border border-blue-100">
                                                    <p className="text-sm font-medium text-blue-900">
                                                        {user && Number(user.role?.id) === RoleEnum.client ? t("clientReviewsTab.clientResponse") : t("clientReviewsTab.officialResponse")}
                                                    </p>
                                                    <p className="mt-1 text-sm text-blue-800">
                                                        {(() => {
                                                            const reply = String(displayReview.reply || "");
                                                            const isExpanded = expandedReviews.has(`${displayReview.id}-reply`);
                                                            const shouldTruncate = reply.length > 200;

                                                            if (!shouldTruncate || isExpanded) {
                                                                return reply;
                                                            }

                                                            return reply.substring(0, 200) + "...";
                                                        })()}
                                                    </p>

                                                    {(() => {
                                                        const reply = String(displayReview.reply || "");
                                                        const isExpanded = expandedReviews.has(`${displayReview.id}-reply`);
                                                        const shouldTruncate = reply.length > 200;

                                                        if (shouldTruncate) {
                                                            return (
                                                                <button
                                                                    onClick={() => toggleReviewExpansion(`${displayReview.id}-reply`)}
                                                                    className="mt-2 text-sm text-blue-600 hover:text-blue-700 hover:underline"
                                                                >
                                                                    {isExpanded ? t("clientReviewsTab.showLess") : t("clientReviewsTab.seeMore")}
                                                                </button>
                                                            );
                                                        }
                                                        return null;
                                                    })()}

                                                    <ReplyVoteButton
                                                        reviewId={displayReview.id}
                                                        hasReply={!!displayReview.reply}
                                                        votes={review.votes || []}
                                                        currentUserId={user?.id}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        );
                    })
                ) : (
                    <div className="text-center py-12">
                        <div className="flex flex-col items-center gap-4">
                            <Star className="h-12 w-12 text-gray-300" />
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-1">{t("clientReviewsTab.noReviewsYet")}</h3>
                                <p className="text-gray-500 text-sm">
                                    {t("clientReviewsTab.noReviewsMessage")}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {shouldShowPagination && (
                <div className="mt-8 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 border-t">
                    <div className="text-sm text-gray-600">
                        {t("clientReviewsTab.showing")} {((currentPage - 1) * reviewsPerPage) + 1} {t("clientReviewsTab.to")} {Math.min(currentPage * reviewsPerPage, userReviewsTotalCount || 0)} {t("clientReviewsTab.of")} {userReviewsTotalCount || 0} {t("clientReviewsTab.reviewsCount")}
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Previous Button */}
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={!hasPreviousPage}
                            className="flex items-center gap-1 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronLeft className="w-4 h-4" />
                            {t("clientReviewsTab.previous")}
                        </button>

                        {/* Page Numbers */}
                        <div className="flex items-center gap-1">
                            {/* Show first page */}
                            {currentPage > 3 && (
                                <>
                                    <button
                                        onClick={() => handlePageChange(1)}
                                        className="px-3 py-2 text-sm border rounded-lg hover:bg-gray-50"
                                    >
                                        1
                                    </button>
                                    {currentPage > 4 && (
                                        <span className="px-2 text-gray-400">...</span>
                                    )}
                                </>
                            )}

                            {/* Show pages around current page */}
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 5) {
                                    pageNum = i + 1;
                                } else if (currentPage <= 3) {
                                    pageNum = i + 1;
                                } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i;
                                } else {
                                    pageNum = currentPage - 2 + i;
                                }

                                if (pageNum < 1 || pageNum > totalPages) return null;

                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => handlePageChange(pageNum)}
                                        className={`px-3 py-2 text-sm border rounded-lg ${
                                            currentPage === pageNum
                                                ? "bg-blue-600 text-white border-blue-600"
                                                : "hover:bg-gray-50"
                                        }`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}

                            {/* Show last page */}
                            {currentPage < totalPages - 2 && (
                                <>
                                    {currentPage < totalPages - 3 && (
                                        <span className="px-2 text-gray-400">...</span>
                                    )}
                                    <button
                                        onClick={() => handlePageChange(totalPages)}
                                        className="px-3 py-2 text-sm border rounded-lg hover:bg-gray-50"
                                    >
                                        {totalPages}
                                    </button>
                                </>
                            )}
                        </div>

                        {/* Next Button */}
                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={!hasNextPage}
                            className="flex items-center gap-1 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {t("clientReviewsTab.next")}
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </Card>
    );
};

export default ClientReviewsTab;
